import {
  ApplySchemaAttributes,
  command,
  CommandFunction,
  extension,
  getTextSelection,
  ExtensionTag,
  isElementDomNode,
  Mark,
  MarkExtension,
  MarkExtensionSpec,
  MarkSpecOverride,
  omitExtraAttributes,
  PrimitiveSelection,
  Static,
  findParentNode,
  getMarkRanges,
  findChildrenByMark,
  helper,
  findElementAtPosition,
  findChildren,
} from '@remirror/core'
import { ExtensionTextHighlightMessages as Messages } from '@remirror/messages'
import { AddMarkStep } from '@remirror/pm/transform'

import type { Helper } from '@remirror/core'

interface TextHighlightOptions {
  /**
   * The default highlight value.
   *
   * @defaultValue ''
   */
  defaultHighlight?: Static<string>
}

export interface TextHighlightAttributes {
  /**
   * The highlight color.
   *
   * @defaultValue ''
   */
  highlight: string
  /**
   * identifier.
   *
   * @defaultValue ''
   */
  comment_id?: string
  status?: string
}

interface HighlightCommandOptions {
  selection?: PrimitiveSelection
}

const setTextHighlightOptions: Remirror.CommandDecoratorOptions = {
  icon: 'markPenLine',
   
  description: ({ t }) => t(Messages.DESCRIPTION),
   
  label: ({ t }) => t(Messages.LABEL),
}

const COMMENT_HIGHLIGHT_ATTRIBUTE = 'comment-highlight-mark'
const COMMENT_HIGHLIGHT_IDENTIFIER = 'comment_id'
const COMMENT_HIGHLIGHT_STATUS = 'status'

/**
 * Add a highlight color to the selected text (or text within a specified
 * range).
 */
@extension<TextHighlightOptions>({
  defaultOptions: {
    defaultHighlight: '',
  },
  staticKeys: ['defaultHighlight'],
  handlerKeys: [],
  customHandlerKeys: [],
})
export class CommentHighlightExtension extends MarkExtension<TextHighlightOptions> {
  get name() {
    return 'commentHighlight' as const
  }

  createTags() {
    return [ExtensionTag.FormattingMark, ExtensionTag.Color]
  }

  createMarkSpec(
    extra: ApplySchemaAttributes,
    override: MarkSpecOverride
  ): MarkExtensionSpec {
    return {
      ...override,
      attrs: {
        highlight: { default: this.options.defaultHighlight },
        comment_id: { default: '' },
        status: { default: '' },
        ...extra.defaults(),
      },
      parseDOM: [
        {
          tag: `comment[${COMMENT_HIGHLIGHT_ATTRIBUTE}]`,
          getAttrs: dom => {
            if (!isElementDomNode(dom)) {
              return null
            }

            const highlight = this.options.defaultHighlight //dom.getAttribute(COMMENT_HIGHLIGHT_ATTRIBUTE);
            const identifier = dom.getAttribute(COMMENT_HIGHLIGHT_IDENTIFIER)
            const status = dom.getAttribute(COMMENT_HIGHLIGHT_STATUS)

            if (!highlight || !identifier) {
              return null
            }
            return {
              ...extra.parse(dom),
              highlight,
              comment_id: identifier,
              status,
            }
          },
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM: (mark: Mark) => {
        const { highlight, comment_id, status, ...other } =
          omitExtraAttributes<TextHighlightAttributes>(mark.attrs, extra)
        const extraAttrs = extra.dom(mark)

        return [
          'comment',
          {
            ...other,
            ...extraAttrs,
            [COMMENT_HIGHLIGHT_ATTRIBUTE]: highlight,
            [COMMENT_HIGHLIGHT_IDENTIFIER]: comment_id,
            [COMMENT_HIGHLIGHT_STATUS]: status,
          },
          0,
        ]
      },
    }
  }
  @command(setTextHighlightOptions)
  setAnnotation(
    comment_id: string,
    options?: HighlightCommandOptions
  ): CommandFunction {
    return this.store.commands.applyMark.original(
      this.type,
      { comment_id },
      options?.selection
    )
  }

  @command(setTextHighlightOptions)
  setUpdateAnnotation(commentId: string, status?: string): CommandFunction {
    return ({ tr, dispatch, state }) => {
      const nodes = findChildren({
        node: state.doc,
        predicate: child => {
          const commentMark = child.node.marks.find(
            mark => mark.attrs.comment_id === commentId
          )
          return commentMark ? true : false
        },
      })

      if (nodes.length === 0) return true

      nodes.forEach(node => {
        const from = node.pos
        const to = node.pos + node.node.nodeSize
        const mark = this.type.create({ comment_id: commentId, status })
        tr.step(new AddMarkStep(from, to, mark))
      })

      dispatch?.(tr)
      return true
    }
  }

  @command()
  clearMarksAnnotation(options?: HighlightCommandOptions): CommandFunction {
    return this.store.commands.removeMark.original({
      type: this.type,
      ...options,
      expand: true,
    })
  }

  @helper()
  getMarksAnnotationForDoc(): Helper<MarkExtensionAnnotation[]> {
    try {
      const state = this.store.getState()
      const nodes = findChildrenByMark({
        node: state.doc,
        type: this.type,
      })
      const res = (nodes ?? [])?.map(item => {
        const commentItem = item?.node?.marks?.find(_it => {
          return _it?.attrs?.comment_id
        })
        return {
          from: item?.pos,
          to: item?.pos + item?.node?.nodeSize,
          text: item?.node?.text ?? '',
          id: commentItem?.attrs?.comment_id,
          status: commentItem?.attrs?.status,
        }
      })

      return res
    } catch (error) {
      return []
    }
  }

  @helper()
  findEndPositionOfSelection(): Helper<number> {
    const { selection } = this.store.getState()
    const parentNode = findParentNode({ predicate: () => true, selection })
    return parentNode?.end ?? 0
  }

  @helper()
  findHtmlElementOnCursor(): Helper<HTMLElement | null> {
    const { selection } = this.store.getState()
    if (Number(selection?.$to?.pos) > -1) {
      return findElementAtPosition(Number(selection?.$to?.pos), this.store.view)
    } else {
      return null
    }
  }

  @helper()
  getMarksAnnotationForSelection(
    position?: PrimitiveSelection
  ): Helper<MarkExtensionAnnotation[]> {
    try {
      const state = this.store.getState()
      const selection = getTextSelection(position ?? state.selection, state.doc)
      const markRange = getMarkRanges(selection, this.type)
      const res = (markRange ?? []).map(item => {
        return {
          from: item?.from,
          to: item?.to,
          text: item?.text,
          id: item?.mark?.attrs?.comment_id,
          status: item?.mark?.attrs?.status,
        }
      })

      return res
    } catch (error) {
      return []
    }
  }
}
